import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import MainLayout from './components/Layout/MainLayout';
import CustomerList from './components/CustomerList';
import CustomerForm from './components/CustomerForm';
import CustomerDetail from './components/CustomerDetail';
import VehicleForm from './components/VehicleForm';
import WorkOrderList from './components/WorkOrder/WorkOrderList';
import ArchivedWorkOrders from './components/WorkOrder/ArchivedWorkOrders';
import WorkOrderForm from './components/WorkOrder/WorkOrderForm';
import OptimizedWorkOrderDetail from './components/WorkOrder/OptimizedWorkOrderDetail';
import EstimateList from './components/EstimateList';
import EstimateForm from './components/EstimateForm';
import EstimateDetail from './components/EstimateDetail';
import TemplateList from './components/Templates/TemplateList';
import Settings from './components/Settings/Settings';
import { TechScheduleView } from './components/Appointments';
import { Scheduling } from './components/Scheduling';
import { TechnicianScheduleSettings } from './components/Settings/TechnicianScheduleSettings';
import { BookingSettings } from './components/Settings/BookingSettings';
import LandingPage from './pages/LandingPage';
import Login from './components/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';
import CustomerPrivateRoute from './components/auth/CustomerPrivateRoute';
import UserManagement from './components/admin/UserManagement';
import OwnerDashboard from './components/dashboards/OwnerDashboard';
import AdvisorDashboard from './components/dashboards/AdvisorDashboard';
import TechnicianDashboard from './components/dashboards/TechnicianDashboard';
import CustomerPortalDashboard from './components/dashboards/CustomerPortalDashboard';
import PublicEstimateView from './pages/PublicEstimateView';
import PublicWorkOrderView from './pages/PublicWorkOrderView';
import PortalInfoPage from './pages/portal/PortalInfoPage';
import PortalEstimatePage from './pages/portal/PortalEstimatePage';
import PortalWorkOrderPage from './pages/portal/PortalWorkOrderPage';
import PortalInvoicePage from './pages/portal/PortalInvoicePage';
import PaymentReturn from './pages/PaymentReturn';
import EstimateTemplatesPage from './pages/EstimateTemplatesPage';
import ServiceCategoryManagerPage from './pages/owner/ServiceCategoryManagerPage';
import OwnerHub from './pages/owner/OwnerHub';
import ReportsPage from './pages/owner/ReportsPage';
import MoneyDashboard from './pages/owner/MoneyDashboard';
import TechRateSettingsPage from './pages/owner/TechRateSettingsPage';
import InvoicePage from './pages/InvoicePage';
import InvoicesPage from './pages/InvoicesPage';
import QuoteRequestsPage from './pages/QuoteRequestsPage';
import ApprovalReviewQueue from './pages/ApprovalReviewQueue';
import RewardSettings from './components/admin/RewardSettings';
import MarketingDashboard from './components/admin/MarketingDashboard';
import SharedPartsBrowser from './pages/admin/SharedPartsBrowser';
import DVITemplatesPage from './pages/settings/DVITemplatesPage';
import DVITemplateBuilderPage from './pages/settings/DVITemplateBuilderPage';
import KnowledgeReviewPage from './pages/settings/KnowledgeReviewPage';
import DiagnosisListsPage from './pages/settings/DiagnosisListsPage';
import InspectionNotePresetsPage from './pages/settings/InspectionNotePresetsPage';
import PromoCodesPage from './pages/settings/PromoCodesPage';
import RequirementPolicyPage from './pages/settings/RequirementPolicyPage';
import { LaborRates } from './components/Settings/LaborRates';
import { PartsMarkups } from './components/Settings/PartsMarkups';
import ReceiptSettings from './components/Settings/ReceiptSettings';
import PromotionList from './components/Promotions/PromotionList';
import PromotionBuilder from './components/Promotions/PromotionBuilder';
import { BookingPortal } from './components/Public/BookingPortal';
import CustomerLoginPage from './pages/CustomerLoginPage';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerVehicles from './pages/CustomerVehicles';
import CustomerRewards from './pages/CustomerRewards';
import CustomerPreferences from './pages/CustomerPreferences';
import CustomerProfile from './pages/CustomerProfile';
import CustomerNotifications from './pages/CustomerNotifications';
import CustomerEstimates from './pages/CustomerEstimates';
import CustomerEstimateDetail from './pages/CustomerEstimateDetail';
import CustomerWorkOrderDetail from './pages/CustomerWorkOrderDetail';
import CustomerHistory from './pages/CustomerHistory';
import CustomerReceipts from './pages/CustomerReceipts';
import CustomerServiceTable from './pages/CustomerServiceTable';
import { logger } from './utils/logger';
import { SessionGuardProvider } from './contexts/SessionGuardContext';
import { SessionActivityProvider } from './contexts/SessionActivityContext';
import { SessionTimeoutModal } from './components/Session/SessionTimeoutModal';
import { RestorePrompt } from './components/Session/RestorePrompt';
import './App.css';
import './styles/designSystem.css';

function SessionGuards({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return (
    <SessionGuardProvider>
      <SessionActivityProvider enabled={isAuthenticated}>
        <SessionTimeoutModal />
        {children}
      </SessionActivityProvider>
    </SessionGuardProvider>
  );
}

/**
 * AdminDashboardRedirect - sends legacy /admin and /admin/dashboard hits to the
 * authenticated user's role-appropriate dashboard instead of the legacy mock.
 * Reuses the same role→dashboard mapping as PrivateRoute and Login.
 */
const AdminDashboardRedirect: React.FC = () => {
  const { user } = useAuth();
  const roleRoutes: Record<string, string> = {
    owner: '/owner/dashboard',
    advisor: '/advisor/dashboard',
    technician: '/technician/dashboard',
    customer: '/portal/dashboard',
  };
  return <Navigate to={(user && roleRoutes[user.role]) || '/login'} replace />;
};

/**
 * `/owner/promotions/:id` — a thin wrapper supplying `PromotionBuilder` its
 * route params via props rather than `useParams`/`useNavigate` directly, so
 * the component itself renders standalone (e.g. under test) without a Router
 * ancestor. The literal id `"new"` means "create a new promotion".
 */
const PromotionBuilderRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <PromotionBuilder
      id={id === 'new' ? undefined : id}
      onSaved={(promotion) => navigate(`/owner/promotions/${promotion.id}`, { replace: true })}
      onBack={() => navigate('/owner/promotions')}
    />
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // PWA functionality is initialized in index.tsx for production builds
    logger.log('[App] Component mounted');

    // Viewport is configured in index.html — no dynamic override

    // Add mobile-specific event listeners
    const handleOnline = () => {
      logger.log('[App] Network back online');
      document.body.classList.remove('offline-mode');
    };

    const handleOffline = () => {
      logger.log('[App] Network went offline');
      document.body.classList.add('offline-mode');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Set initial offline state
    if (!navigator.onLine) {
      document.body.classList.add('offline-mode');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#2563eb',
          colorPrimaryHover: '#1d4ed8',
          colorPrimaryActive: '#1e40af',
          borderRadius: 8,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: 14,
          controlHeight: 44,
          colorBgLayout: '#f9fafb',
          colorBgContainer: '#ffffff',
          colorBorder: '#e5e7eb',
          colorText: '#111827',
          colorTextSecondary: '#6b7280',
        },
        components: {
          Button: {
            borderRadius: 8,
            controlHeight: 44,
            fontWeight: 500,
          },
          Card: {
            borderRadius: 12,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          },
          Input: {
            borderRadius: 8,
            controlHeight: 44,
          },
          Select: {
            borderRadius: 8,
            controlHeight: 44,
          },
        },
      }}
    >
      <AntdApp>
        <AuthProvider>
          <CustomerAuthProvider>
            <NotificationProvider>
              <SessionGuards>
                <Router>
                <RestorePrompt />
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/services" element={<Navigate to="/book" replace />} />
                  <Route path="/book" element={<BookingPortal />} />
                  <Route path="/booking" element={<BookingPortal />} />
                  <Route path="/estimate/:token" element={<PublicEstimateView />} />
                  <Route path="/work-order/:token" element={<PublicWorkOrderView />} />
                  <Route path="/payment/return" element={<PaymentReturn />} />
                  <Route path="/payment/success" element={<PaymentReturn />} />
                  <Route path="/payment/canceled" element={<Navigate to="/" replace />} />

                  {/* Public portal token routes — NO auth required; token in URL is the auth */}
                  <Route path="/portal/info/:token" element={<PortalInfoPage />} />
                  <Route path="/portal/estimate/:token" element={<PortalEstimatePage />} />
                  <Route path="/portal/workorder/:token" element={<PortalWorkOrderPage />} />
                  <Route path="/portal/invoice/:token" element={<PortalInvoicePage />} />

                  {/* Customer Portal Routes */}
                  <Route path="/customer/login" element={<CustomerLoginPage />} />
                  <Route path="/customer/dashboard" element={<CustomerPrivateRoute><CustomerDashboard /></CustomerPrivateRoute>} />
                  <Route path="/customer/vehicles" element={<CustomerPrivateRoute><CustomerVehicles /></CustomerPrivateRoute>} />
                  <Route path="/customer/rewards" element={<CustomerPrivateRoute><CustomerRewards /></CustomerPrivateRoute>} />
                  <Route path="/customer/preferences" element={<CustomerPrivateRoute><CustomerPreferences /></CustomerPrivateRoute>} />
                  <Route path="/customer/profile" element={<CustomerPrivateRoute><CustomerProfile /></CustomerPrivateRoute>} />
                  <Route path="/customer/notifications" element={<CustomerPrivateRoute><CustomerNotifications /></CustomerPrivateRoute>} />
                  <Route path="/customer/estimates" element={<CustomerPrivateRoute><CustomerEstimates /></CustomerPrivateRoute>} />
                  <Route path="/customer/history" element={<CustomerPrivateRoute><CustomerHistory /></CustomerPrivateRoute>} />
                  <Route path="/customer/receipts" element={<CustomerPrivateRoute><CustomerReceipts /></CustomerPrivateRoute>} />
                  <Route path="/customer/vehicles/:id/service-table" element={<CustomerPrivateRoute><CustomerServiceTable /></CustomerPrivateRoute>} />
                  {/* Customer estimate/work-order detail by numeric id (id-based, customer-auth, read-only) */}
                  <Route path="/customer/estimates/:id" element={<CustomerPrivateRoute><CustomerEstimateDetail /></CustomerPrivateRoute>} />
                  <Route path="/customer/work-orders/:id" element={<CustomerPrivateRoute><CustomerWorkOrderDetail /></CustomerPrivateRoute>} />

                  {/* Legacy route redirect */}
                  <Route path="/admin/login" element={<Navigate to="/login" replace />} />

                  {/* Owner Routes */}
                  <Route path="/owner/*" element={
                    <PrivateRoute allowedRoles={['owner']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<OwnerDashboard />} />
                          <Route path="/users" element={<UserManagement />} />
                          <Route path="/customers" element={<CustomerList />} />
                          <Route path="/customers/new" element={<CustomerForm />} />
                          <Route path="/customers/:id" element={<CustomerDetail />} />
                          <Route path="/customers/:id/edit" element={<CustomerForm />} />
                          <Route path="/estimates" element={<EstimateList />} />
                          <Route path="/estimates/new" element={<EstimateForm />} />
                          <Route path="/estimates/:id" element={<EstimateDetail />} />
                          <Route path="/estimates/:id/edit" element={<EstimateForm />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/archived" element={<ArchivedWorkOrders />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/appointments" element={<Scheduling />} />
                          <Route path="/appointments/schedule" element={<TechScheduleView />} />
                          <Route path="/templates" element={<TemplateList />} />
                          <Route path="/estimate-templates" element={<EstimateTemplatesPage />} />
                          <Route path="/quote-requests" element={<QuoteRequestsPage />} />
                          {/* Staff review of customer approval submissions (spec §7). Owner and
                              advisor only — the server refuses a technician, and the UI must not
                              offer a door that answers 403. */}
                          <Route path="/approval-review" element={<ApprovalReviewQueue />} />
                          <Route path="/work-orders/new" element={<WorkOrderForm />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/settings/technician-schedules" element={<TechnicianScheduleSettings />} />
                          <Route path="/settings/booking-settings" element={<BookingSettings />} />
                          <Route path="/settings/rewards" element={<RewardSettings />} />
                          <Route path="/settings/dvi-templates" element={<DVITemplatesPage />} />
                          <Route path="/settings/dvi-templates/:id" element={<DVITemplateBuilderPage />} />
                          <Route path="/settings/knowledge-review" element={<KnowledgeReviewPage />} />
                          <Route path="/settings/diagnosis-lists" element={<DiagnosisListsPage />} />
                          <Route path="/settings/inspection-note-presets" element={<InspectionNotePresetsPage />} />
                          <Route path="/settings/promo-codes" element={<PromoCodesPage />} />
                          {/* Customer information requirements (spec §10.1). Owner-only on
                              purpose: PUT /completeness/policy answers 403 to an advisor, so
                              this route lives under /owner/* and nowhere else. */}
                          <Route path="/settings/requirements" element={<RequirementPolicyPage />} />
                          <Route path="/settings/labor-rates" element={<LaborRates />} />
                          <Route path="/settings/parts-markups" element={<PartsMarkups />} />
                          {/* Owner-only for the same reason as requirements above: the
                              PUT is owner-gated server-side (requireOwner), so this route
                              lives under /owner/* only. */}
                          <Route path="/settings/receipts" element={<ReceiptSettings />} />
                          {/* Owner-only, same reasoning as receipts above: create/update/
                              margin on /api/admin/promotions are requireOwner server-side. */}
                          <Route path="/promotions" element={<PromotionList />} />
                          <Route path="/promotions/:id" element={<PromotionBuilderRoute />} />
                          <Route path="/shared-parts" element={<SharedPartsBrowser />} />
                          <Route path="/marketing" element={<MarketingDashboard />} />
                          <Route path="/reports" element={<ReportsPage />} />
                          <Route path="/money" element={<MoneyDashboard />} />
                          <Route path="/settings/tech-rates" element={<TechRateSettingsPage />} />
                          <Route path="/calendar" element={<Navigate to="/owner/appointments" replace />} />
                          <Route path="/hub" element={<OwnerHub />} />
                          <Route path="/service-categories" element={<ServiceCategoryManagerPage />} />
                          {/* The list comes first. Until it existed, "what is unpaid" had
                              no answer in the app: an invoice opened only if you already
                              knew its id, and this path fell through to the dashboard. */}
                          <Route path="/invoices" element={<InvoicesPage />} />
                          <Route path="/invoices/:id" element={<InvoicePage />} />
                          {/* Other unmatched owner routes → dashboard, not a blank page */}
                          <Route path="*" element={<Navigate to="/owner/dashboard" replace />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Advisor Routes */}
                  <Route path="/advisor/*" element={
                    <PrivateRoute allowedRoles={['owner', 'advisor']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<AdvisorDashboard />} />
                          <Route path="/customers" element={<CustomerList />} />
                          <Route path="/customers/new" element={<CustomerForm />} />
                          <Route path="/customers/:id" element={<CustomerDetail />} />
                          <Route path="/customers/:id/edit" element={<CustomerForm />} />
                          <Route path="/estimates" element={<EstimateList />} />
                          <Route path="/estimates/new" element={<EstimateForm />} />
                          <Route path="/estimates/:id" element={<EstimateDetail />} />
                          <Route path="/estimates/:id/edit" element={<EstimateForm />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/archived" element={<ArchivedWorkOrders />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/appointments" element={<Scheduling />} />
                          <Route path="/appointments/schedule" element={<TechScheduleView />} />
                          <Route path="/calendar" element={<Navigate to="/advisor/appointments" replace />} />
                          <Route path="/templates" element={<TemplateList />} />
                          <Route path="/estimate-templates" element={<EstimateTemplatesPage />} />
                          <Route path="/quote-requests" element={<QuoteRequestsPage />} />
                          {/* Staff review of customer approval submissions (spec §7). Owner and
                              advisor only — the server refuses a technician, and the UI must not
                              offer a door that answers 403. */}
                          <Route path="/approval-review" element={<ApprovalReviewQueue />} />
                          <Route path="/shared-parts" element={<SharedPartsBrowser />} />
                          <Route path="/invoices/:id" element={<InvoicePage />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Technician Routes */}
                  <Route path="/technician/*" element={
                    <PrivateRoute allowedRoles={['owner', 'advisor', 'technician']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<TechnicianDashboard />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/schedule" element={<TechScheduleView />} />
                          <Route path="/appointments" element={<TechScheduleView />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Customer Portal Routes */}
                  <Route path="/portal/*" element={
                    <PrivateRoute allowedRoles={['customer']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<CustomerPortalDashboard />} />
                          <Route path="/estimates" element={<div>My Estimates - Coming Soon</div>} />
                          <Route path="/vehicles" element={<div>My Vehicles - Coming Soon</div>} />
                          <Route path="/history" element={<div>Service History - Coming Soon</div>} />
                          <Route path="/payments" element={<div>Payments - Coming Soon</div>} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Legacy /admin routes - redirect dashboard to role-based home; settings owner-only */}
                  <Route path="/admin/*" element={
                    <PrivateRoute>
                      <MainLayout>
                        <Routes>
                          {/* Legacy mock dashboard → redirect to the user's role dashboard */}
                          <Route path="/" element={<AdminDashboardRedirect />} />
                          <Route path="/dashboard" element={<AdminDashboardRedirect />} />
                          <Route path="/customers" element={<CustomerList />} />
                          <Route path="/customers/new" element={<CustomerForm />} />
                          <Route path="/customers/:id" element={<CustomerDetail />} />
                          <Route path="/customers/:id/edit" element={<CustomerForm />} />
                          <Route path="/customers/:id/vehicles/new" element={<VehicleForm />} />
                          <Route path="/customers/:id/vehicles/:vehicleId/edit" element={<VehicleForm />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/new" element={<WorkOrderForm />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/estimates" element={<EstimateList />} />
                          <Route path="/estimates/new" element={<EstimateForm />} />
                          <Route path="/estimates/:id" element={<EstimateDetail />} />
                          <Route path="/estimates/:id/edit" element={<EstimateForm />} />
                          <Route path="/appointments" element={<Scheduling />} />
                          <Route path="/appointments/schedule" element={<TechScheduleView />} />
                          <Route path="/templates" element={<TemplateList />} />
                          {/* Settings routes - owner-only (legacy /admin settings were reachable by any role) */}
                          <Route path="/settings" element={
                            <PrivateRoute allowedRoles={['owner']}><Settings /></PrivateRoute>
                          } />
                          <Route path="/settings/technician-schedules" element={
                            <PrivateRoute allowedRoles={['owner']}><TechnicianScheduleSettings /></PrivateRoute>
                          } />
                          <Route path="/settings/booking-settings" element={
                            <PrivateRoute allowedRoles={['owner']}><BookingSettings /></PrivateRoute>
                          } />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />
                </Routes>
                </Router>
              </SessionGuards>
            </NotificationProvider>
          </CustomerAuthProvider>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
